// arsip.repo — tiket terarsip (Owner).
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { currentUser } from '@core/session';
import { logAudit } from '@core/audit';

export interface ArsipRow { no: string; status: string; unit: string; custNama: string; teknisi: string; tglAmbil: string; voided: boolean; voidReason: string | null; }
const nm = (c: { nama: string } | { nama: string }[] | null): string =>
  (Array.isArray(c) ? c[0]?.nama : c?.nama) ?? '—';

interface Raw { no: string; status: string; unit: string | null; teknisi: string | null; tgl_ambil_iso: string | null; voided: boolean | null; void_reason: string | null; customers: { nama: string } | { nama: string }[] | null }

export async function listArchived(): Promise<ArsipRow[]> {
  const rows = await fetchAllPaged<Raw>((a, b) => supabase()
    .from('tickets').select('no,status,unit,teknisi,tgl_ambil_iso,voided,void_reason,customers(nama)')
    // Tiket yang DIHAPUS Owner (mode "pertahankan data keuangan") sengaja TIDAK muncul di sini:
    // bagi pengguna ia sudah dihapus, bukan diarsipkan (keputusan Apo 28 Jul). Barisnya tetap ada
    // agar laporan keuangan tak berubah surut; jejaknya di Audit Log.
    .eq('archived', true).is('deleted_at', null)
    .order('tgl_ambil_iso', { ascending: false }).order('no').range(a, b)) as unknown as Raw[];
  return rows.map((r) => ({
    no: r.no, status: r.status, unit: r.unit ?? '—', custNama: nm(r.customers), teknisi: r.teknisi ?? '—',
    tglAmbil: r.tgl_ambil_iso ?? '—', voided: r.voided ?? false, voidReason: r.void_reason,
  }));
}

// ── Arsip masal per bulan/tahun (+wilayah) — port v0.18 bulkArchive/arsMatch ────────────
// Operasional-only (`archived`): sembunyi dari operasional, KEUANGAN TETAP UTUH (finScope pakai arsip_keu).
// Hanya transaksi SELESAI (Sudah Diambil) — tak menyentuh tiket aktif ("data depan" aman).
export interface BulkCand { no: string; custNama: string; unit: string; tglAmbil: string; outlet: string }
interface BulkRaw { no: string; unit: string | null; outlet: string; tgl_ambil_iso: string | null; tgl_iso: string | null; customers: { nama: string } | { nama: string }[] | null }

/** arsMatch v0.18: cocok bila (tahun/bulan cocok pada tgl ambil||terima) & wilayah cocok. */
const arsMatch = (iso: string | null, year: number | null, month: number | null): boolean => {
  if (!year && !month) return true;
  if (!iso) return false;
  const d = new Date(iso);
  if (year && d.getFullYear() !== year) return false;
  if (month && d.getMonth() + 1 !== month) return false;
  return true;
};

/** Kandidat arsip masal (Sudah Diambil, belum diarsip) yang cocok filter periode/wilayah. */
export async function bulkArchiveCandidates(year: number | null, month: number | null, outlet: string | null): Promise<BulkCand[]> {
  const rows = await fetchAllPaged<BulkRaw>((a, b) => {
    let q = supabase().from('tickets')
      .select('no,unit,outlet,tgl_ambil_iso,tgl_iso,customers(nama)')
      .eq('status', 'Sudah Diambil').eq('archived', false);
    if (outlet) q = q.eq('outlet', outlet);
    return q.order('tgl_ambil_iso', { ascending: false }).order('no').range(a, b);
  }) as unknown as BulkRaw[];
  return rows
    .filter((r) => arsMatch(r.tgl_ambil_iso ?? r.tgl_iso, year, month))
    .map((r) => ({ no: r.no, custNama: nm(r.customers), unit: r.unit ?? '—', tglAmbil: r.tgl_ambil_iso ?? '—', outlet: r.outlet }));
}

/** Arsipkan masal daftar `nos` (O/M). Batch agar tak menabrak batas URL; +log per tiket. */
export async function bulkArchive(nos: string[], label: string): Promise<void> {
  const u = currentUser();
  if (!u || (u.role !== 'Owner' && u.role !== 'Manager')) throw new Error('Hanya Owner/Manager yang dapat mengarsipkan.'); // canArsip v0.18
  if (!nos.length) return;
  const by = u.nama;
  for (let i = 0; i < nos.length; i += 100) {
    const chunk = nos.slice(i, i + 100);
    const { error } = await supabase().from('tickets').update({ archived: true }).in('no', chunk);
    if (error) throw new Error(error.message);
    await supabase().from('ticket_logs').insert(chunk.map((no) => ({ ticket_no: no, status: 'Arsip', catatan: `Arsip masal (${label})`, by_user: by })));
  }
  await logAudit({ action: 'ARCHIVE', module: 'ticket', refId: `BULK ${nos.length} (${label})` }); // jejak aksi sensitif
}

export async function unarchive(no: string): Promise<void> {
  const u = currentUser();
  if (!u || (u.role !== 'Owner' && u.role !== 'Manager')) throw new Error('Hanya Owner/Manager yang dapat mengembalikan dari arsip.'); // A3
  // A1: reset voided + arsip_keu juga → tiket ter-VOID tak terperangkap 1 arah (kembali ke operasional + keuangan).
  const { error } = await supabase().from('tickets').update({ archived: false, voided: false, arsip_keu: false, void_reason: null }).eq('no', no);
  if (error) throw new Error(error.message);
  await supabase().from('ticket_logs')
    .insert({ ticket_no: no, status: 'Arsip', catatan: 'Dikembalikan dari arsip', by_user: currentUser()?.nama ?? '' });
}
